import { useEffect, useRef } from "react";

/** A focus and a visibilitychange that arrive together (switching back to the tab fires both) count as one. */
const EVENT_DEBOUNCE_MS = 1000;

/**
 * D183.6: calls `onTick` every `intervalMs` (default 60 s) only while the tab is visible, and once at once when the
 * tab becomes visible again or the window regains focus. No tick while hidden. `enabled: false` stops everything.
 * The latest `onTick` is always the one called, so a caller may pass a fresh closure every render.
 *
 * This is a trigger only: the caller decides what a tick reads and when it must not (unsaved edits).
 */
export function useVisiblePolling(opts: { onTick: () => void; intervalMs?: number; enabled?: boolean }): void {
  const { intervalMs = 60_000, enabled = true } = opts;
  const tickRef = useRef(opts.onTick);
  tickRef.current = opts.onTick;

  useEffect(() => {
    if (!enabled) return undefined;
    let lastEventTick = -Infinity;
    const isVisible = () => document.visibilityState === "visible";
    const onEvent = () => {
      if (!isVisible()) return;
      const now = Date.now();
      if (now - lastEventTick < EVENT_DEBOUNCE_MS) return;
      lastEventTick = now;
      tickRef.current();
    };
    const timer = window.setInterval(() => {
      if (isVisible()) tickRef.current();
    }, intervalMs);
    document.addEventListener("visibilitychange", onEvent);
    window.addEventListener("focus", onEvent);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onEvent);
      window.removeEventListener("focus", onEvent);
    };
  }, [intervalMs, enabled]);
}
